import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './DanceGoodsPage.css';
import { BaseUrl } from '../../BaseUrl';
import ProductDetailModal from './ProductDetailModal';
import ApiService from '../../services/ApiService';
import useLike from '../../hooks/useLike';

import shoesIcon from './icons/shoes.png';
import outfitsIcon from './icons/outfits.png';
import accessoriesIcon from './icons/accessories.png';
import classesIcon from './icons/classes.png';
import healthIcon from './icons/health.png';
import equipmentIcon from './icons/equipment.png';

const API_BASE = `${BaseUrl}/api/v1`;

const PREDEFINED_CATEGORIES = [
  'Shoes',
  'Outfits',
  'Accessories',
  'Classes',
  'Health',
  'Equipment',
];

const getCategoryImage = (category) => {
  const images = {
    'Shoes': shoesIcon,
    'Outfits': outfitsIcon,
    'Accessories': accessoriesIcon,
    'Classes': classesIcon,
    'Health': healthIcon,
    'Equipment': equipmentIcon,
  };
  return images[category] || null;
};

const getCategoryColor = (category) => {
  const colors = {
    'Shoes': '#E8734A',
    'Outfits': '#F0923A',
    'Accessories': '#D4A843',
    'Classes': '#D4A632',
    'Health': '#7DA882',
    'Equipment': '#3D7A7A',
  };
  return colors[category] || '#9B59B6';
};

const DanceGoodsPage = () => {
  const navigate = useNavigate();
  const [goods, setGoods] = useState([]);
  const [topPicks, setTopPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [searchQuery, setSearchQuery] = useState('');
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [danceStyles, setDanceStyles] = useState([]);
  const [selectedDanceStyle, setSelectedDanceStyle] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const ITEMS_PER_PAGE = 12;

  // Check if user is logged in
  const isLoggedIn = useCallback(() => {
    return localStorage.getItem('authToken') || localStorage.getItem('token');
  }, []);

  // Like/save hook for goods
  const {
    toggleLike,
    isLiked,
    getLikeCount,
    initializeBatch,
    setLiked,
  } = useLike({
    entityType: 'good',
    isLoggedIn,
    onAuthRequired: () => setShowLoginModal(true),
    onError: (msg) => console.error(msg),
  });

  const fetchGoods = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/goods/community?sort=${sortBy}&page=${page}&limit=${ITEMS_PER_PAGE}`;
      if (selectedCategory) {
        url += `&category=${encodeURIComponent(selectedCategory)}`;
      }
      if (selectedDanceStyle) {
        url += `&danceStyle=${encodeURIComponent(selectedDanceStyle)}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        setGoods(data.data || []);
        setCategories(data.categories || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotal(data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Error fetching community goods:', error);
      setGoods([]);
    } finally {
      setLoading(false);
    }
  }, [sortBy, page, selectedCategory, selectedDanceStyle]);

  const fetchTopPicks = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/goods/community?sort=popular&limit=3`);
      const data = await response.json();
      if (data.success) {
        setTopPicks(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching top picks:', error);
    }
  }, []);

  const fetchCategoryCounts = useCallback(async () => {
    try {
      const counts = {};
      for (const cat of PREDEFINED_CATEGORIES) {
        const response = await fetch(`${API_BASE}/goods/community?category=${encodeURIComponent(cat)}&limit=1`);
        const data = await response.json();
        if (data.success) {
          counts[cat] = data.pagination?.total || 0;
        }
      }
      setCategoryCounts(counts);
    } catch (error) {
      console.error('Error fetching category counts:', error);
    }
  }, []);

  const fetchBrands = useCallback(async () => {
    try {
      const url = selectedCategory
        ? `${API_BASE}/goods/brands?category=${encodeURIComponent(selectedCategory)}`
        : `${API_BASE}/goods/brands`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setBrands(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchGoods();
  }, [fetchGoods]);

  useEffect(() => {
    fetchTopPicks();
    fetchCategoryCounts();
    // Fetch dance styles
    fetch(`${API_BASE}/goods/dance-styles`)
      .then(res => res.json())
      .then(data => { if (data.success) setDanceStyles(data.data || []); })
      .catch(() => {});
  }, [fetchTopPicks, fetchCategoryCounts]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  useEffect(() => {
    setPage(1);
  }, [selectedCategory, sortBy, selectedBrand, selectedDanceStyle]);

  // Initialize like states when goods load and fetch user's liked goods
  useEffect(() => {
    if (goods.length === 0) return;

    initializeBatch(
      goods,
      () => false,
      (good) => good.likeCount || 0
    );

    const fetchUserLikes = async () => {
      if (!isLoggedIn()) return;
      try {
        const goodIds = goods.map(g => g._id);
        const response = await ApiService.post('goods/check-likes', { goodIds });
        const likeStatus = response?.data?.data || {};
        Object.entries(likeStatus).forEach(([goodId, status]) => {
          if (status && status.liked) {
            setLiked(goodId, true);
          }
        });
      } catch (error) {
        console.error('Error fetching good likes:', error);
      }
    };

    fetchUserLikes();
  }, [goods, isLoggedIn, initializeBatch, setLiked]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(prev => prev === category ? '' : category);
  };

  const filteredGoods = goods.filter(g => {
    if (searchQuery && !g.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (selectedBrand && g.brand !== selectedBrand) return false;
    return true;
  });

  // ============================================================================
  // RENDER: PRODUCT CARD (simplified — no comments)
  // ============================================================================

  const renderProductCard = (good) => {
    const categoryColor = getCategoryColor(good.categories?.[0]);
    const categoryImg = getCategoryImage(good.categories?.[0]);

    return (
      <div key={good._id} className="goods-card" onClick={() => setSelectedProductId(good._id)}>
        <div className="goods-card-accent" style={{ background: categoryColor }} />
        <div className="goods-card-body">
          <div className="goods-card-header">
            {!selectedCategory && (
              <div className="goods-card-icon">
                {categoryImg && <img src={categoryImg} alt={good.categories?.[0] || ''} className="goods-card-icon-img" />}
              </div>
            )}
            <div className="goods-card-title-area">
              <h3 className="goods-card-title">{good.title}</h3>
              <div className="goods-card-tags">
                <span
                  className="goods-card-category"
                  style={{ background: `${categoryColor}15`, color: categoryColor }}
                >
                  {good.categories?.[0] || 'General'}
                </span>
                {good.brand && (
                  <span
                    className="goods-card-brand"
                    onClick={(e) => { e.stopPropagation(); setSelectedBrand(good.brand); }}
                  >
                    {good.brand}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="goods-card-footer">
            <div className="goods-card-social">
              <i className="bi bi-people-fill"></i>
              <span>{good.recommendationCount} dancer{good.recommendationCount !== 1 ? 's' : ''} recommend{good.recommendationCount === 1 ? 's' : ''} this</span>
            </div>
            <button
              className={`goods-like-btn ${isLiked(good._id) ? 'liked' : ''}`}
              onClick={(e) => { e.stopPropagation(); toggleLike(good._id); }}
              title={isLiked(good._id) ? 'Remove from saved' : 'Save to library'}
            >
              <i className={isLiked(good._id) ? 'bi bi-heart-fill' : 'bi bi-heart'}></i>
              {getLikeCount(good._id) > 0 && (
                <span className="goods-like-count">{getLikeCount(good._id)}</span>
              )}
            </button>
          </div>

        </div>
      </div>
    );
  };

  // ============================================================================
  // RENDER: TOP PICK CARD (ribbon style — horizontal)
  // ============================================================================

  const renderTopPickCard = (good) => {
    const categoryImg = getCategoryImage(good.categories?.[0]);

    return (
      <div
        key={good._id}
        className="top-pick-card"
        onClick={() => setSelectedProductId(good._id)}
      >
        <div className="top-pick-icon">
          {categoryImg && <img src={categoryImg} alt={good.categories?.[0] || ''} className="top-pick-icon-img" />}
        </div>
        <div className="top-pick-info">
          <h4 className="top-pick-title">{good.title}</h4>
          <span className="top-pick-recs">
            <i className="bi bi-people-fill"></i> {good.recommendationCount} recommendation{good.recommendationCount !== 1 ? 's' : ''}
          </span>
        </div>
        <i className="bi bi-box-arrow-up-right top-pick-arrow"></i>
      </div>
    );
  };

  // ============================================================================
  // RENDER: PAGE
  // ============================================================================

  return (
    <div className="dance-goods-page">
      {/* Hero Section */}
      <section className="dg-hero">
        <div className="dg-hero-content">
          <h1 className="dg-hero-title">
            Recommended by <span className="dg-highlight">Dancers</span>,
            <br />for Dancers
          </h1>
          <p className="dg-hero-subtitle">
            Real products trusted and recommended by our dance community.
            Every item here has been personally vouched for by dancers like you.
          </p>
          <p className="dg-hero-note">
            * No Amazon links will be included in our library. Support communities in dance and beyond.
          </p>
        </div>
      </section>

      {/* Top Picks Ribbon */}
      {topPicks.length > 0 && !selectedCategory && !searchQuery && (
        <section className="dg-top-ribbon">
          <div className="dg-container">
            <h2 className="dg-ribbon-title">Community Favorites</h2>
            <div className="dg-ribbon-cards">
              {topPicks.map(good => renderTopPickCard(good))}
            </div>
          </div>
        </section>
      )}

      {/* Main Content: Split Layout */}
      <section className="dg-main">
        <div className="dg-container">
          <div className="dg-split-layout">

            {/* LEFT: Products (3/4) */}
            <div className="dg-products-col">
              {/* Filter Bar */}
              <div className="dg-filter-bar">
                <div className="dg-search">
                  <i className="bi bi-search"></i>
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <div className="dg-filter-controls">
                  {brands.length > 0 && (
                    <select
                      value={selectedBrand}
                      onChange={(e) => setSelectedBrand(e.target.value)}
                      className="dg-select"
                    >
                      <option value="">All Brands</option>
                      {brands.map(b => (
                        <option key={b.brand} value={b.brand}>
                          {b.brand} ({b.productCount})
                        </option>
                      ))}
                    </select>
                  )}

                  {danceStyles.length > 0 && (
                    <select
                      value={selectedDanceStyle}
                      onChange={(e) => setSelectedDanceStyle(e.target.value)}
                      className="dg-select"
                    >
                      <option value="">All Dance Styles</option>
                      {danceStyles.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  )}

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="dg-select"
                  >
                    <option value="popular">Most Recommended</option>
                    <option value="recent">Recently Added</option>
                    <option value="oldest">Oldest First</option>
                  </select>
                </div>

                {(selectedCategory || searchQuery || selectedBrand || selectedDanceStyle) && (
                  <button
                    className="dg-clear-filters"
                    onClick={() => { setSelectedCategory(''); setSearchQuery(''); setSelectedBrand(''); setSelectedDanceStyle(''); }}
                  >
                    <i className="bi bi-x-circle"></i> Clear
                  </button>
                )}
              </div>

              {total > 0 && (
                <p className="dg-results-count">
                  {selectedCategory || searchQuery || selectedBrand || selectedDanceStyle
                    ? `Showing ${filteredGoods.length} result${filteredGoods.length !== 1 ? 's' : ''}`
                    : `${total} product${total !== 1 ? 's' : ''} recommended by the community`
                  }
                </p>
              )}

              {/* Products Grid */}
              {loading ? (
                <div className="dg-loading">
                  <div className="dg-spinner"></div>
                  <p>Loading recommendations...</p>
                </div>
              ) : filteredGoods.length === 0 ? (
                <div className="dg-empty">
                  <i className="bi bi-bag-heart"></i>
                  <h3>No Products Yet</h3>
                  <p>
                    {selectedCategory
                      ? `No recommendations in "${selectedCategory}" yet.`
                      : 'Be the first to recommend a product to the community!'
                    }
                  </p>
                  <button
                    className="dg-cta-btn"
                    onClick={() => navigate('/profile?section=goods')}
                  >
                    Add a Recommendation
                  </button>
                </div>
              ) : (
                <>
                  <div className="dg-products-grid">
                    {filteredGoods.map(good => renderProductCard(good))}
                  </div>

                  {totalPages > 1 && (
                    <div className="dg-pagination">
                      <button
                        className="dg-page-btn"
                        disabled={page <= 1}
                        onClick={() => setPage(p => p - 1)}
                      >
                        <i className="bi bi-chevron-left"></i> Previous
                      </button>
                      <span className="dg-page-info">
                        Page {page} of {totalPages}
                      </span>
                      <button
                        className="dg-page-btn"
                        disabled={page >= totalPages}
                        onClick={() => setPage(p => p + 1)}
                      >
                        Next <i className="bi bi-chevron-right"></i>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* RIGHT: Categories Sidebar (1/4) */}
            <aside className="dg-sidebar">
              <h3 className="dg-sidebar-title">Categories</h3>
              <div className="dg-sidebar-tiles">
                {PREDEFINED_CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    className={`dg-sidebar-tile ${selectedCategory === cat ? 'active' : ''}`}
                    onClick={() => handleCategoryClick(cat)}
                    style={{ '--cat-color': getCategoryColor(cat) }}
                  >
                    <div className="dg-sidebar-tile-icon">
                      <img src={getCategoryImage(cat)} alt={cat} className="dg-sidebar-tile-img" />
                    </div>
                    <div className="dg-sidebar-tile-info">
                      <span className="dg-sidebar-tile-name">{cat}</span>
                      {categoryCounts[cat] > 0 && (
                        <span className="dg-sidebar-tile-count">{categoryCounts[cat]}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {/* Sidebar CTA */}
              <div className="dg-sidebar-cta">
                <p>Know a great dance product?</p>
                <button
                  className="dg-sidebar-cta-btn"
                  onClick={() => navigate('/profile?section=goods')}
                >
                  <i className="bi bi-plus-lg"></i> Add Yours
                </button>
              </div>
            </aside>

          </div>
        </div>
      </section>

      {/* Product Detail Modal */}
      {selectedProductId && (
        <ProductDetailModal
          productId={selectedProductId}
          onClose={() => setSelectedProductId(null)}
          onProductClick={(id) => setSelectedProductId(id)}
          getCategoryImage={getCategoryImage}
          getCategoryColor={getCategoryColor}
          isLiked={isLiked}
          toggleLike={toggleLike}
          getLikeCount={getLikeCount}
        />
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <div className="login-modal-backdrop show" onClick={() => setShowLoginModal(false)}>
          <div className="login-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="close-btn"
              onClick={() => setShowLoginModal(false)}
            >
              <i className="bi bi-x-lg"></i>
            </button>
            <i className="bi bi-heart-fill"></i>
            <h3>Save to Your Library</h3>
            <p>Sign in to save products to your library and build your personal dance collection.</p>
            <Link to="/login" className="login-btn">
              <i className="bi bi-box-arrow-in-right"></i> Sign In
            </Link>
            <div className="login-divider"><span>or</span></div>
            <p className="text-muted small mb-2">
              Don't have an account?{' '}
              <Link to="/catalogue" style={{ color: 'var(--purple-accent)' }}>Sign up</Link>
            </p>
            <button
              className="skip-link"
              onClick={() => setShowLoginModal(false)}
            >
              Continue browsing without signing in
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DanceGoodsPage;
